import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { fetchRenewals } from "../lib/dataFetchers";
import type { Region } from "../lib/types";
import { FilterBar, FilterGroup, SegmentedControl } from "../components/ui";

type SeverityFilter = "all" | "red" | "orange" | "yellow" | "green";

export default function Renewals(): JSX.Element {
  const { t } = useTranslation("renewals");
  const [region, setRegion] = useState<Region | "ALL">("ALL");
  const [type, setType] = useState<"ALL" | "License" | "Company" | "AnnualFiling" | "EOR">("ALL");
  const [severity, setSeverity] = useState<SeverityFilter>("all");

  // PPP §B5 — 6 new region values. Labels are i18n keys (en + fr) so French
  // operators see French region names; previous hardcoded English breached
  // CLAUDE.md §i18n.
  const REGION_FILTERS: { value: Region | "ALL"; label: string }[] = [
    { value: "ALL",           label: t("region_all") },
    { value: "EUROPE",        label: t("region_europe") },
    { value: "MIDDLE_EAST",   label: t("region_middle_east") },
    { value: "AFRICA",        label: t("region_africa") },
    { value: "NORTH_AMERICA", label: t("region_north_america") },
    { value: "LATIN_AMERICA", label: t("region_latin_america") },
    { value: "ASIA_PACIFIC",  label: t("region_asia_pacific") },
  ];

  const TYPE_FILTERS: { value: "ALL" | "License" | "Company" | "AnnualFiling" | "EOR"; label: string }[] = [
    { value: "ALL",         label: t("type_all") },
    { value: "License",     label: t("filter_license") },
    { value: "Company",     label: t("filter_company") },
    { value: "AnnualFiling",label: t("filter_annual_filing") },
    { value: "EOR",         label: t("filter_eor") },
  ];

  // PPP §A5 — 3-tier severity filter pills
  const SEVERITY_FILTERS: { value: SeverityFilter; label: string }[] = [
    { value: "all",    label: t("filter_all") },
    { value: "red",    label: t("filter_red") },
    { value: "orange", label: t("filter_orange") },
    { value: "yellow", label: t("filter_yellow") },
    { value: "green",  label: t("filter_green") },
  ];

  const { data, isLoading } = useQuery({
    queryKey: ["renewals", { region, type }],
    queryFn: () =>
      fetchRenewals({
        region: region === "ALL" ? undefined : region,
        type: type === "ALL" ? undefined : type,
      }),
  });

  // Show ALL renewals, each with its correct day count. The "60-day horizon"
  // refers only to the headline count + the timeline below — see `within60`.
  const renewals = useMemo(() => data ?? [], [data]);

  // Headline count = renewals due within 60 days (the urgent horizon window).
  const within60 = useMemo(
    () => renewals.filter((r) => r.daysUntil <= 60),
    [renewals],
  );

  // 4-tier grouping by daysUntil — Red (≤30) / Orange (31–60) / Yellow (61–90) / Green (>90)
  const groups = useMemo(() => {
    const expired = renewals.filter((r) => r.daysUntil < 0);
    const red     = renewals.filter((r) => r.daysUntil >= 0 && r.daysUntil <= 30);
    const orange  = renewals.filter((r) => r.daysUntil > 30 && r.daysUntil <= 60);
    const yellow  = renewals.filter((r) => r.daysUntil > 60 && r.daysUntil <= 90);
    const green   = renewals.filter((r) => r.daysUntil > 90);
    return { expired, red, orange, yellow, green };
  }, [renewals]);

  // Client-side severity filter on top of data
  const filtered = useMemo(() => {
    if (severity === "all") return renewals;
    if (severity === "red") return [...groups.expired, ...groups.red];
    if (severity === "orange") return groups.orange;
    if (severity === "yellow") return groups.yellow;
    return groups.green;
  }, [renewals, severity, groups]);

  const byMonth = useMemo(() => {
    const map = new Map<string, typeof renewals>();
    for (const r of filtered) {
      const d = new Date(r.parsedDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page header — proto .page-h */}
      <div className="flex items-end justify-between gap-[14px] mb-[18px] flex-wrap">
        <div>
          <h1 className="text-[24px] leading-[1.1] m-0">{t("page_title")}</h1>
          <p className="text-[13px] text-muted mt-[5px]">
            {renewals.some((r) => r.daysUntil <= 30) ? (
              <span className="text-red font-semibold">
                {t("page_subtitle", { count: within60.length })}
              </span>
            ) : (
              t("page_subtitle", { count: within60.length })
            )}
          </p>
        </div>
      </div>

      {/* Filter bar — proto .filterbar with 3 .fgroup/.seg */}
      <FilterBar>
        <FilterGroup label={t("region_filter_label")}>
          <SegmentedControl<Region | "ALL">
            options={REGION_FILTERS}
            value={region}
            onChange={setRegion}
            label={t("region_filter_label")}
          />
        </FilterGroup>
        <FilterGroup label={t("type_filter_label")}>
          <SegmentedControl<"ALL" | "License" | "Company" | "AnnualFiling" | "EOR">
            options={TYPE_FILTERS}
            value={type}
            onChange={setType}
            label={t("type_filter_label")}
          />
        </FilterGroup>
        <FilterGroup label={t("severity_filter_label")}>
          <SegmentedControl<SeverityFilter>
            options={SEVERITY_FILTERS}
            value={severity}
            onChange={setSeverity}
            label={t("severity_filter_label")}
          />
        </FilterGroup>
      </FilterBar>

      {/* Legend bar — proto .legend-bar */}
      <div className="flex gap-4 flex-wrap bg-surface border border-border rounded-[10px] p-[9px_15px] shadow-card mb-[6px]">
        {/* Proto legend uses static colour descriptions — no i18n keys exist for these */}
        <span className="flex items-center gap-[6px] text-[11.5px] text-muted">
          <span aria-hidden className="w-[9px] h-[9px] rounded-full bg-red shrink-0" />
          Red = overdue or due ≤30 days
        </span>
        <span className="flex items-center gap-[6px] text-[11.5px] text-muted">
          <span aria-hidden className="w-[9px] h-[9px] rounded-full bg-amber shrink-0" />
          Orange = 31–60 days
        </span>
        <span className="flex items-center gap-[6px] text-[11.5px] text-muted">
          <span aria-hidden className="w-[9px] h-[9px] rounded-full bg-yellow shrink-0" />
          Yellow = 61–90 days
        </span>
        <span className="flex items-center gap-[6px] text-[11.5px] text-muted">
          <span aria-hidden className="w-[9px] h-[9px] rounded-full bg-green shrink-0" />
          Green = more than 90 days
        </span>
      </div>

      {/* KPI cards — proto .kpis. Explicit 5-col on desktop so all 5 severity
          tiles fit in one row even when the AI sidebar narrows content width. */}
      <div className="grid gap-[13px] my-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard
          borderColor="var(--red)"
          dotColor="var(--red)"
          label={t("kpi_expired")}
          count={isLoading ? "…" : String(groups.expired.length)}
          hint="Past their renewal date"
        />
        <KpiCard
          borderColor="var(--red)"
          dotColor="var(--red)"
          label={t("kpi_lt30")}
          count={isLoading ? "…" : String(groups.red.length)}
          hint="Urgent — act now"
        />
        <KpiCard
          borderColor="var(--amber)"
          dotColor="var(--amber)"
          label={t("kpi_lt60")}
          count={isLoading ? "…" : String(groups.orange.length)}
          hint="Plan ahead"
        />
        <KpiCard
          borderColor="var(--yellow)"
          dotColor="var(--yellow)"
          label={t("kpi_lt90")}
          count={isLoading ? "…" : String(groups.yellow.length)}
          hint="Further out"
        />
        <KpiCard
          borderColor="var(--green)"
          dotColor="var(--green)"
          label={t("kpi_gt90")}
          count={isLoading ? "…" : String(groups.green.length)}
          hint="Long-range"
        />
      </div>

      {/* 60-day timeline panel — proto .panel .card-h .cal-wrap .timeline */}
      <div className="bg-surface border border-border rounded-[11px] shadow-card overflow-hidden mb-4">
        <div className="flex items-center gap-2 px-4 py-[13px] border-b border-border-2">
          <h3 className="text-[12.5px] font-bold tracking-[.03em] uppercase text-muted">
            {t("timeline_title")}
          </h3>
        </div>
        <div className="p-[18px]">
          {/* Timeline track */}
          <div
            className="relative overflow-hidden rounded-[10px] border border-border-2"
            style={{
              height: 92,
              background: "linear-gradient(90deg, var(--red-soft), transparent 40%)",
            }}
          >
            {/* Gridlines + labels */}
            {[0, 15, 30, 45, 60].map((d) => {
              const L = (d / 60) * 96 + 2;
              return (
                <span key={d}>
                  <div
                    aria-hidden
                    className="absolute top-0 bottom-5 w-px bg-border-2"
                    style={{ left: `${L}%` }}
                  />
                  <span
                    aria-hidden
                    className="absolute bottom-1 text-[10px] text-dim"
                    style={{ left: `${L}%`, transform: "translateX(-50%)" }}
                  >
                    +{d}d
                  </span>
                </span>
              );
            })}
            {/* Today line */}
            <div
              aria-hidden
              className="absolute top-0 bottom-5 w-[2px] bg-accent opacity-50"
              style={{ left: "2%" }}
            />

            {/* Dots */}
            {renewals
              .filter((r) => r.daysUntil >= 0 && r.daysUntil <= 60)
              .map((r, i) => {
                const left = (r.daysUntil / 60) * 96 + 2;
                const top = 26 + (i % 2) * 22;
                const bg =
                  r.severity === "red" ? "var(--red)"
                  : r.severity === "orange" ? "var(--amber)"
                  : "var(--green)";
                return (
                  <Link
                    key={r.id}
                    to={`/entities/${r.entityId}`}
                    title={`${r.entityName} · ${r.daysUntil}d`}
                    className="absolute w-3 h-3 rounded-full border-2 border-surface shadow-sm hover:scale-125 transition-transform"
                    style={{
                      left: `${left}%`,
                      top,
                      background: bg,
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                );
              })}
          </div>
        </div>
      </div>

      {/* Monthly buckets — proto .month-grp / .r-row */}
      <div className="space-y-4">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton h-[52px]" />
          ))
        ) : byMonth.length === 0 ? (
          <div
            className="text-[13px] text-dim p-[18px] text-center border border-dashed border-border rounded-[9px] bg-surface"
          >
            {severity === "all" ? t("empty_state") : t("no_results_hint")}
          </div>
        ) : (
          byMonth.map(([key, list]) => {
            const [y, m] = key.split("-");
            if (!y || !m) return null;
            const monthLabel = new Date(+y, +m - 1, 1).toLocaleDateString(i18n.language, {
              month: "long",
              year: "numeric",
            });
            return (
              <div key={key} className="mt-4">
                {/* Month header — proto .month-h */}
                <div className="flex items-center gap-[9px] text-[12.5px] font-bold text-muted px-0.5 py-[5px]">
                  <svg
                    aria-hidden
                    className="w-[13px] h-[13px] text-dim shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                  {monthLabel}
                  <span className="ml-auto text-[11px] font-semibold text-dim bg-surface-2 rounded-full px-[9px] py-0.5 tabular-nums">
                    {list.length}
                  </span>
                </div>

                {/* Renewal rows — proto .r-row */}
                <div className="space-y-2 mt-1">
                  {list.map((r) => {
                    // Colour by daysUntil bucket (backend severity only has
                    // red/orange/green; the yellow 61–90 tier is frontend-derived).
                    const sevColor =
                      r.daysUntil <= 30
                        ? { bg: "var(--red-soft)", text: "var(--red-text)" }
                        : r.daysUntil <= 60
                          ? { bg: "var(--amber-soft)", text: "var(--amber-text)" }
                          : r.daysUntil <= 90
                            ? { bg: "var(--yellow-soft)", text: "var(--yellow-text)" }
                            : { bg: "var(--green-soft)", text: "var(--green-text)" };

                    const typeLabel =
                      r.type === "License"
                        ? t("type_license")
                        : r.type === "AnnualFiling"
                          ? t("type_annual_filing")
                          : r.type === "EOR"
                            ? t("type_eor")
                            : t("type_company");

                    return (
                      <div
                        key={r.id}
                        className="flex items-center gap-3 bg-surface border border-border rounded-[10px] p-[11px_15px] shadow-card hover:shadow-card-hov transition-shadow"
                      >
                        {/* Flag */}
                        <span
                          aria-hidden
                          className="text-[20px] leading-none shrink-0"
                        >
                          {r.countryFlag}
                        </span>

                        {/* Info */}
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/entities/${r.entityId}`}
                            className="text-[13.5px] font-semibold text-text hover:text-accent transition-colors"
                          >
                            {r.entityName}
                          </Link>
                          <p className="text-[12px] text-muted mt-0.5">
                            {typeLabel} · {r.rawValue}
                          </p>
                        </div>

                        {/* Right section */}
                        <div className="flex items-center gap-[10px] ml-auto shrink-0">
                          {/* Days badge — always show the countdown (or Expired) */}
                          <span
                            className="text-[11.5px] font-bold px-[9px] py-[3px] rounded-[8px] tabular-nums whitespace-nowrap"
                            style={{
                              background: sevColor.bg,
                              color: sevColor.text,
                            }}
                          >
                            {r.daysUntil < 0 ? t("severity_expired") : `${r.daysUntil}d`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── KPI card — proto .kpi ──────────────────────────────────────────────────────
function KpiCard({
  borderColor,
  dotColor,
  label,
  count,
  hint,
}: {
  borderColor: string;
  dotColor: string;
  label: string;
  count: string;
  hint: string;
}): JSX.Element {
  return (
    <div
      className="bg-surface border border-border rounded-[11px] shadow-card p-[14px_16px]"
      style={{ borderLeftColor: borderColor, borderLeftWidth: 4 }}
    >
      {/* .k-top */}
      <div className="flex items-center gap-[7px] text-[11px] font-bold tracking-[.03em] uppercase text-muted">
        <span
          aria-hidden
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: dotColor }}
        />
        {label}
      </div>
      {/* .k-num */}
      <div className="font-display text-[27px] font-semibold mt-[5px] tabular-nums text-text">
        {count}
      </div>
      {/* .k-hint */}
      <div className="text-[11px] text-dim mt-0.5">{hint}</div>
    </div>
  );
}
